import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Users, Globe, Award, Target, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEO, { breadcrumbSchema } from '../components/SEO';
import InquiryForm from '../components/InquiryForm';

const About = () => {
  const stats = [
    { value: '5000+', label: 'Happy Travelers', icon: Users },
    { value: '50+', label: 'Countries Covered', icon: Globe },
    { value: '10+', label: 'Years Experience', icon: Award },
    { value: '98%', label: 'Success Rate', icon: Target }
  ];

  const values = [
    { title: 'Customer First', description: 'Your satisfaction is our top priority', icon: Heart },
    { title: 'Transparency', description: 'Honest pricing with no hidden charges', icon: CheckCircle },
    { title: 'Expertise', description: 'Years of experience in travel industry', icon: Award },
    { title: 'Global Reach', description: 'Connections across the world', icon: Globe }
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="About Varni Visa - Trusted Visa Consultant in Surat, Gujarat"
        description="Learn about Varni Visa - Surat's premier visa consultancy and travel agency. Years of experience helping travelers with Dubai, Schengen, Thailand visas and international tours."
        keywords="About Varni Visa, Visa Consultant Surat, Travel Agency Surat, Varni Visa Gujarat"
        path="/about"
        schema={breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'About Us', path: '/about' }
        ])}
      />
      {/* Hero */}
      <section 
        className="relative h-[400px] flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(45, 141, 180, 0.85), rgba(247, 148, 93, 0.75)), url('https://images.pexels.com/photos/7079773/pexels-photo-7079773.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')`
        }}
      >
        <div className="container mx-auto px-4 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">About Varni Visa</h1>
          <p className="text-xl">Your trusted partner for global travel solutions</p>
        </div>
      </section>

      {/* Story */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#F7945D] font-semibold uppercase tracking-wider text-sm mb-2">Our Story</p>
              <h2 className="text-4xl font-bold mb-6 text-gray-900">India HQ. Thailand operations.</h2>
              <p className="text-gray-700 mb-4">
                Varni Visa is a trusted visa and travel company based in <strong>Surat, India</strong>. Through our Thailand travel division, <strong>Varni2Fly</strong>, we provide direct destination management services and local support in <strong>Pattaya, Thailand</strong>.
              </p>
              <p className="text-gray-700 mb-4">
                This vertical integration allows us to offer <strong>competitive pricing, reliable service and personalised travel experiences</strong> for both leisure and business travellers — without depending on third-party handlers.
              </p>
              <p className="text-gray-700 mb-6">
                From visa applications and international tour packages to flight & hotel bookings, Thailand DMC services, group tours, customised holidays, airport transfers and corporate travel — we&apos;re a complete travel solution under one roof.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/thailand-dmc">
                  <Button className="bg-[#2D8DB4] hover:bg-[#247a9a]">Explore Varni2Fly DMC</Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline">Get in Touch</Button>
                </Link>
              </div>
            </div>
            <div>
              <img 
                src="https://images.unsplash.com/photo-1454496406107-dc34337da8d6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwzfHx2aXNhJTIwcGFzc3BvcnR8ZW58MHx8fHwxNzgwMjM3NDg2fDA&ixlib=rb-4.1.0&q=85"
                alt="Travel passport"
                className="rounded-lg shadow-xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-gradient-to-r from-[#2D8DB4] to-[#F7945D] text-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {stats.map((stat, idx) => {
              const Icon = stat.icon;
              return (
                <div key={idx}>
                  <Icon className="mx-auto mb-4" size={40} />
                  <div className="text-4xl font-bold mb-2">{stat.value}</div>
                  <div className="text-sm">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card>
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold mb-4 text-[#2D8DB4]">Our Mission</h3>
                <p className="text-gray-600">
                  To provide hassle-free, transparent, and reliable visa and travel services that enable our customers to explore the world with confidence. We aim to be the most trusted travel partner for individuals and businesses alike.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold mb-4 text-[#F7945D]">Our Vision</h3>
                <p className="text-gray-600">
                  To become India's leading travel and visa consultancy by delivering exceptional service, building lasting relationships, and making international travel accessible to everyone.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Our Core Values</h2>
            <p className="text-gray-600">The principles that guide everything we do</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, idx) => {
              const Icon = value.icon;
              return (
                <Card key={idx} className="text-center hover:shadow-xl transition-shadow">
                  <CardContent className="p-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#2D8DB4] to-[#F7945D] rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon className="text-white" size={28} />
                    </div>
                    <h3 className="text-xl font-semibold mb-2">{value.title}</h3>
                    <p className="text-gray-600 text-sm">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Inquiry */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-3xl font-bold text-center mb-2">Let&apos;s Plan Your Next Journey</h2>
          <p className="text-center text-gray-600 mb-8">Reach out to our team — we&apos;re here to help</p>
          <InquiryForm showTypeSelector />
        </div>
      </section>
    </div>
  );
};

export default About;
